import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SalesOrderInfo } from '../../../store/slices/salesOrders';
import classes from './ScheduleEntry.module.css';
import moment from 'moment/moment';
import editIcon from '../../../assets/icons/edit-appointment.svg';
import { useNavigate } from 'react-router-dom';
import { UpdateAppointmentModal } from './UpdateAppointmentModal';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { clearProductTimeslots, selectUserRole } from '../../../store/slices/auth';
import { NURSE_USER_ROLE } from '../../../constants/userRolesConstants';
import { ConsultationTypes } from '../../../enums/enums';
import closeIcon from '../../../assets/icons/close-icon-dark.svg';
import arrowRight from '../../../assets/icons/arrow-right.svg';

import { IDoctor, selectDoctors } from '../../../store/slices/doctors';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  salesOrder?: SalesOrderInfo;
  timeSlot: string;
  handleUpdateAppointment: (
    salesOrder: SalesOrderInfo,
    start: Date,
    end: Date,
    callback: () => void
  ) => void;
  loadingUpdate?: boolean;
  // multiple appointments on the same time
  salesOrders?: SalesOrderInfo[];
};

const ScheduleEntry: React.FC<Props> = ({
  salesOrder,
  timeSlot,
  handleUpdateAppointment,
  loadingUpdate,
  salesOrders,
}) => {
  const navigate = useNavigate();
  const [updateAppointment, setUpdateAppointment] = useState<boolean>(false);
  const [salesOrderToUpdate, setSalesOrderToUpdate] = useState<SalesOrderInfo | undefined>();
  const userRole: string = useAppSelector<string>(selectUserRole);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { translations } = useTranslation();
  const popupRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const doctors: IDoctor[] = useAppSelector<IDoctor[]>(selectDoctors);
  const isLeftSideSlot = parseInt(timeSlot.split(':')[1]) >= 30;

  useEffect(() => {
    if (!updateAppointment) {
      dispatch(clearProductTimeslots());
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node) && isPopupOpen) {
        setIsPopupOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopupOpen, updateAppointment]);

  const doctorAssignedFullName = useMemo(() => {
    const tempoDoctorAssigned = doctors.find((doctor) =>
      doctor.person_appointments.find((appointment) => {
        if (
          salesOrder?.sales_order_item.sales_order_id &&
          salesOrder?.sales_order_item.sales_order_item_id
        ) {
          return (
            appointment.sales_order_id == salesOrder.sales_order_item.sales_order_id &&
            appointment.sales_order_item_id == salesOrder.sales_order_item.sales_order_item_id
          );
        }
      })
    );
    if (tempoDoctorAssigned) {
      return `${tempoDoctorAssigned.person_firstname} ${tempoDoctorAssigned.person_lastname}`;
    }
    return undefined;
  }, [
    doctors,
    salesOrder?.sales_order_item.sales_order_id,
    salesOrder?.sales_order_item.sales_order_item_id,
  ]);

  const isDiagnostic =
    salesOrder?.service_service_group.service_group_id === 1 ||
    salesOrder?.service_service_group.service_group_id === 4;
  const isNurseInClinik =
    salesOrder?.service.service_id === ConsultationTypes.IN_CLINIK_CONSULTATION &&
    userRole === NURSE_USER_ROLE &&
    (salesOrder?.work_order?.work_order_status_id === 5 ||
      salesOrder?.work_order?.work_order_status_id === 6);
  if (!salesOrder) {
    return (
      <div className={classes.container}>
        <div className={classes.startTime} style={{ opacity: 0.4 }}>
          {timeSlot}
        </div>
      </div>
    );
  }

  const isClosed =
    salesOrder.work_order?.work_order_status_id == 2 ||
    salesOrder.work_order?.work_order_status_id == 3;

  const checkIsDiagnostic = (so: SalesOrderInfo): boolean => {
    return (
      so?.service_service_group.service_group_id === 1 ||
      so?.service_service_group.service_group_id === 4
    );
  };

  return (
    <div
      className={[
        !isClosed ? classes.container : classes.containerDisabled,
        isDiagnostic
          ? classes.diagnostic
          : doctorAssignedFullName
          ? classes.consultation
          : classes.notAssigned,
      ].join(' ')}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', flex: 1, width: '100%' }}
        onClick={() => {
          if (salesOrders && salesOrders?.length > 1) {
            setIsPopupOpen(true);
            return;
          }
          isNurseInClinik
            ? navigate('/web/prejoin-consultation', {
                state: {
                  ...salesOrder,
                  person: salesOrder?.customer_person,
                  sales_order: salesOrder?.sales_order,
                  sales_order_item: salesOrder?.sales_order_item,
                  sales_order_appointment: salesOrder?.sales_order_appointment,
                  service: salesOrder?.service,
                  work_order: {
                    ...salesOrder?.work_order,
                    work_order_id: salesOrder?.work_order?.work_order_status_id,
                  },
                },
              })
            : navigate(
                isDiagnostic
                  ? `/web/schedule?selectedTab=check-in&accountNumber=${salesOrder.customer_person.person_account_number}&salesOrderId=${salesOrder.sales_order.sales_order_id}&salesOrderItemId=${salesOrder?.sales_order_item?.sales_order_item_id}`
                  : `/web/consultation/${salesOrder.sales_order.sales_order_id}/${salesOrder?.sales_order_item?.sales_order_item_id}`
              );
        }}
      >
        <div className={classes.customerInfo}>
          <div className={classes.customerName}>
            {salesOrder.customer_person.person_firstname}{' '}
            {salesOrder.customer_person.person_lastname}
          </div>
          {!isDiagnostic && (
            <div className={classes.doctorName}>
              {doctorAssignedFullName
                ? `${translations('label.assigned')} ${doctorAssignedFullName}`
                : translations('label_no.doctor.assigned')}
            </div>
          )}
          <div className={classes.startTime}>
            {moment(salesOrder.sales_order_appointment.start).format('HH:mm')}
          </div>
        </div>
        <div className={classes.clockIcon}>
          <div className={classes.length}>
            {moment(salesOrder.sales_order_appointment.end_time).diff(
              moment(salesOrder.sales_order_appointment.start),
              'minutes'
            )}{' '}
            min.
          </div>
        </div>
      </div>
      <div
        className={classes.editIcon}
        onClick={(event) => {
          event.stopPropagation();
          setUpdateAppointment(true);
          setSalesOrderToUpdate(salesOrder);
        }}
      >
        <img alt={'edit'} src={editIcon} />
      </div>
      {updateAppointment && (
        <UpdateAppointmentModal
          salesOrder={salesOrderToUpdate ?? salesOrder}
          isOpen={updateAppointment}
          onClose={() => {
            setUpdateAppointment(false);
            setSalesOrderToUpdate(undefined);
          }}
          handleUpdateAppointment={(start, end) => {
            handleUpdateAppointment(salesOrderToUpdate ?? salesOrder, start, end, () => {
              setUpdateAppointment(false);
              setSalesOrderToUpdate(undefined);
            });
          }}
          loadingUpdate={!!loadingUpdate}
        />
      )}
      {salesOrders && salesOrders?.length > 1 && (
        <div onClick={() => setIsPopupOpen(true)} className={classes.moreLabel}>{`${
          salesOrders.length - 1
        } ${translations('more')}`}</div>
      )}
      {isPopupOpen && (
        <div
          className={classes.popupContainer}
          style={isLeftSideSlot ? { right: '102%' } : { left: '102%' }}
          ref={popupRef}
        >
          <div className={classes.popupHeader}>
            <div className={classes.popupTitle}>
              {moment(salesOrder.sales_order_appointment.start).format('dd.MM.yyyy')}
            </div>
            <img
              src={closeIcon}
              alt="close"
              className={classes.popupCloseButton}
              onClick={() => setIsPopupOpen(false)}
            />
          </div>
          <div className={classes.popupAppointmentsCount}>{`${salesOrders?.length} ${translations(
            'appointments'
          )}`}</div>
          <div>
            {salesOrders?.map((so, index) => (
              <div
                key={index}
                className={[
                  classes.popupItemContainer,
                  checkIsDiagnostic(so)
                    ? classes.diagnostic
                    : !so.sales_order_appointment.person_assign_consutation_name
                    ? classes.notAssigned
                    : classes.consultation,
                ].join(' ')}
                onClick={() => {
                  isNurseInClinik
                    ? navigate('/web/prejoin-consultation', {
                        state: {
                          ...so,
                          person: so?.customer_person,
                          sales_order: so?.sales_order,
                          sales_order_item: so?.sales_order_item,
                          sales_order_appointment: so?.sales_order_appointment,
                          service: so?.service,
                          work_order: {
                            ...so?.work_order,
                            work_order_id: so?.work_order?.work_order_status_id,
                          },
                        },
                      })
                    : navigate(
                        checkIsDiagnostic(so)
                          ? `/web/schedule?selectedTab=check-in&accountNumber=${so.customer_person.person_account_number}&salesOrderId=${so.sales_order.sales_order_id}&salesOrderItemId=${so?.sales_order_item?.sales_order_item_id}`
                          : `/web/consultation/${so.sales_order.sales_order_id}/${so?.sales_order_item?.sales_order_item_id}`
                      );
                }}
              >
                <div className={classes.popupItemPart}>{timeSlot}</div>
                <div
                  className={classes.popupItemPart}
                >{`${so.customer_person.person_firstname} ${so.customer_person.person_lastname}`}</div>
                <div className={classes.popupItemPart}>15 min</div>
                <div className={classes.popupItemPart}>{so.service.service_name}</div>
                <img
                  className={classes.popupEdit}
                  alt={'edit'}
                  src={editIcon}
                  onClick={(event) => {
                    event.stopPropagation();
                    setUpdateAppointment(true);
                    setSalesOrderToUpdate(so);
                  }}
                />
                <img src={arrowRight} alt="right" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { ScheduleEntry };
